import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth, API } from "@/App";
import axios from "axios";
import {
  Bell, ArrowRight, UserPlus, Camera,
  Sparkles, Send, Users, Info, Check, LifeBuoy, Bug, Lightbulb
} from "lucide-react";

const ICON_MAP = {
  "arrow-right": ArrowRight,
  "user-plus": UserPlus,
  "camera": Camera,
  "sparkles": Sparkles,
  "send": Send,
  "users": Users,
  "info": Info,
  "life-buoy": LifeBuoy,
  "bug": Bug,
  "lightbulb": Lightbulb,
};

const COLOR_MAP = {
  stage_advanced: "#2A9D9D",
  new_lead: "#f59e0b",
  crew_photo: "#10b981",
  concept_ready: "#8b5cf6",
  draft_sent: "#3b82f6",
  crew_added: "#3b82f6",
  support_reply: "#2A9D9D",
  system: "#64748b",
  admin_support_ticket: "#2A9D9D",
  admin_bug_report: "#ef4444",
  admin_feature_request: "#8b5cf6",
  admin_new_signup: "#f59e0b",
};

const TYPE_ICON = {
  stage_advanced: "arrow-right",
  new_lead: "user-plus",
  crew_photo: "camera",
  concept_ready: "sparkles",
  draft_sent: "send",
  crew_added: "users",
  support_reply: "life-buoy",
  admin_support_ticket: "life-buoy",
  admin_bug_report: "bug",
  admin_feature_request: "lightbulb",
  admin_new_signup: "user-plus",
};

// Map a notification to the URL it should open when clicked.
function notificationLink(n) {
  if (n.link) return n.link;
  if (n.type === "support_reply") return "/help";
  if (n.project_id) return `/projects/${n.project_id}`;
  return null;
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  return `${days}d ago`;
}

export default function NotificationBell() {
  const { token } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const ref = useRef(null);

  useEffect(() => {
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (open) fetchNotifications();
  }, [open]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const fetchUnreadCount = async () => {
    try {
      const res = await axios.get(`${API}/notifications/unread-count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setUnreadCount(res.data.count);
    } catch { /* silent */ }
  };

  const fetchNotifications = async () => {
    try {
      const res = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(res.data);
    } catch { /* silent */ }
  };

  const markRead = async (id) => {
    try {
      await axios.put(`${API}/notifications/${id}/read`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => n.id === id ? { ...n, read: true } : n));
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch { /* silent */ }
  };

  const markAllRead = async () => {
    try {
      await axios.put(`${API}/notifications/read-all`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      setUnreadCount(0);
    } catch { /* silent */ }
  };

  return (
    <div ref={ref} className="relative" data-testid="notification-bell">
      {/* Bell Button */}
      <button
        onClick={() => setOpen(!open)}
        data-testid="notification-bell-btn"
        className="relative p-2 rounded-lg text-slate-400 hover:text-white hover:bg-white/5 transition-all"
      >
        <Bell className="w-5 h-5" />
        {unreadCount > 0 && (
          <span className="absolute -top-0.5 -right-0.5 w-4.5 h-4.5 min-w-[18px] px-1 flex items-center justify-center rounded-full bg-red-500 text-white text-[9px] font-bold leading-none">
            {unreadCount > 9 ? "9+" : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {open && (
        <div className="absolute right-0 top-full mt-2 w-80 sm:w-96 bg-[#11161F] border border-white/10 rounded-xl shadow-2xl shadow-black/40 overflow-hidden z-50 animate-fade-in" data-testid="notification-dropdown">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-white/[0.06]">
            <span className="text-white text-sm font-bold">Notifications</span>
            {unreadCount > 0 && (
              <button onClick={markAllRead} className="flex items-center gap-1 text-[#2A9D9D] text-[10px] font-semibold uppercase tracking-wider hover:text-white transition-colors" data-testid="mark-all-read">
                <Check className="w-3 h-3" /> Mark all read
              </button>
            )}
          </div>

          {/* List */}
          <div className="max-h-[380px] overflow-y-auto scrollbar-thin">
            {notifications.length === 0 ? (
              <div className="py-10 text-center">
                <Bell className="w-8 h-8 text-slate-700 mx-auto mb-2" />
                <p className="text-slate-500 text-sm">No notifications yet</p>
              </div>
            ) : (
              notifications.map(n => {
                const color = COLOR_MAP[n.type] || "#64748b";
                const IconComp = ICON_MAP[TYPE_ICON[n.type] || "info"] || Info;
                const target = notificationLink(n);

                return (
                  <button
                    key={n.id}
                    onClick={() => {
                      if (!n.read) markRead(n.id);
                      if (target) {
                        setOpen(false);
                        navigate(target);
                      }
                    }}
                    data-testid={`notification-${n.id}`}
                    className={`w-full flex items-start gap-3 px-4 py-3 text-left transition-colors ${
                      n.read ? "bg-transparent" : "bg-white/[0.02]"
                    } hover:bg-white/[0.04]`}
                  >
                    <div className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5"
                      style={{ background: `${color}15` }}>
                      <IconComp className="w-4 h-4" style={{ color }} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className={`text-xs font-semibold truncate ${n.read ? "text-slate-400" : "text-white"}`}>
                          {n.title}
                        </span>
                        {!n.read && <div className="w-1.5 h-1.5 rounded-full bg-[#2A9D9D] flex-shrink-0" />}
                      </div>
                      <p className="text-slate-500 text-[11px] mt-0.5 truncate">{n.message}</p>
                      <span className="text-slate-600 text-[10px] mt-0.5 block">{timeAgo(n.created_at)}</span>
                    </div>
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
}
